/**
 * Helpers for mirroring page state into the URL query string without trampling
 * params the page does not own, and without re-writing the URL when the owned
 * params already match. Used by the quest workspace/events/claims pages to keep
 * their filters, sorting, pagination, and open modals deep-linkable.
 *
 * The contract: a page declares the set of param keys it "owns". Everything
 * else on the URL is preserved verbatim.
 */

type ParamSource = URLSearchParams | Record<string, string | undefined>;

function readParam(
  source: ParamSource,
  key: string,
): string | null | undefined {
  return source instanceof URLSearchParams ? source.get(key) : source[key];
}

/**
 * A stable, order-independent signature of just the owned params. Comparing the
 * signature of the current URL against the signature of the desired params tells
 * us whether a write is actually needed — which is what stops the URL↔state sync
 * from looping. Keys are projected in the fixed `keys` order so the signature is
 * deterministic regardless of how the params happen to be ordered on the URL.
 */
export function ownedParamsSignature(
  source: ParamSource,
  keys: readonly string[],
): string {
  const projected: Record<string, string> = {};
  for (const key of keys) {
    const value = readParam(source, key);
    if (value != null) {
      projected[key] = value;
    }
  }
  return JSON.stringify(projected);
}

/**
 * Returns a new URLSearchParams equal to `current` but with every owned key
 * cleared and then re-set from `desired`. Foreign params (anything not in
 * `keys`) are left untouched, and owned keys absent from `desired` are dropped.
 */
export function mergeOwnedParams(
  current: URLSearchParams,
  keys: readonly string[],
  desired: Record<string, string>,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const key of keys) {
    next.delete(key);
  }
  for (const key of keys) {
    const value = desired[key];
    if (value !== undefined) {
      next.set(key, value);
    }
  }
  return next;
}
